import React, { useEffect, useRef, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { authenticatedIdentity } from '../../_services/apiFetch';
import { machiningMaintenanceService as service } from '../../_services/machiningMaintenance.service';

export function AssetMaintenancePhotos({
    kind,
    parentType,
    parentId,
    taskSeq,
    section,
    revision,
    attachmentIds,
    onChange,
    onStateChange,
    onConflict,
    disabled,
    readOnly,
    displayAsLinks,
}) {
    const [images, setImages] = useState({});
    const [loadError, setLoadError] = useState('');
    const [loadAttempt, setLoadAttempt] = useState(0);
    const [pending, setPending] = useState(false);
    const [failure, setFailure] = useState('');
    const [sessionValid, setSessionValid] = useState(true);
    const retryFile = useRef(null);
    const stateCallback = useRef(onStateChange);
    const changeCallback = useRef(onChange);
    changeCallback.current = onChange;
    const currentIds = useRef(attachmentIds);
    const mutationSequence = useRef(0);
    currentIds.current = attachmentIds;
    stateCallback.current = onStateChange;
    const controller = useRef(null);
    const mounted = useRef(true);
    const initialIdentity = useRef(authenticatedIdentity());
    const ids = attachmentIds.join(',');
    const purpose =
        parentType === 'schedule'
            ? 'reference'
            : parentType === 'request'
              ? `${section} evidence`
              : 'completion';
    const label = parentType === 'request' ? `${section} evidence` : `task ${taskSeq} ${purpose}`;

    useEffect(() => {
        if (readOnly || !parentId || !sessionValid) return;

        let active = true;
        const sequence = mutationSequence.current;
        service
            .listImages(kind, { parentType, parentId, taskSeq, section })
            .then(({ items }) => {
                if (
                    !active ||
                    sequence !== mutationSequence.current ||
                    initialIdentity.current !== authenticatedIdentity()
                )
                    return;

                const recovered = items
                    .map((item) => item.id)
                    .filter((id) => !currentIds.current.includes(id));
                if (recovered.length) changeCallback.current([...currentIds.current, ...recovered]);
            })
            .catch((error) => {
                if (active) setLoadError(error.message);
            });

        return () => {
            active = false;
        };
    }, [kind, parentType, parentId, taskSeq, section, readOnly, sessionValid, loadAttempt]);

    useEffect(() => {
        const valid = sessionValid;
        const abort = new AbortController();
        const urls = [];
        let active = true;
        setImages({});
        setLoadError('');
        if (valid && attachmentIds.length) {
            Promise.all(
                attachmentIds.map(async (id) => {
                    const blob = await service.getImage(kind, id, abort.signal);
                    if (!active || initialIdentity.current !== authenticatedIdentity()) return;

                    const url = URL.createObjectURL(blob);
                    urls.push(url);
                    setImages((previous) => ({ ...previous, [id]: url }));
                })
            ).catch((error) => {
                if (active && error.name !== 'AbortError') setLoadError(error.message);
            });
        }

        return () => {
            active = false;
            abort.abort();
            urls.forEach((url) => URL.revokeObjectURL(url));
        };
    }, [kind, ids, sessionValid, loadAttempt]);

    useEffect(() => {
        mounted.current = true;
        function checkSession() {
            if (authenticatedIdentity() !== initialIdentity.current) {
                controller.current?.abort();
                setSessionValid(false);
                retryFile.current = null;
            }
        }
        const timer = setInterval(checkSession, 1000);
        window.addEventListener('storage', checkSession);

        return () => {
            mounted.current = false;
            controller.current?.abort();
            clearInterval(timer);
            window.removeEventListener('storage', checkSession);
            stateCallback.current?.(false);
        };
    }, []);

    useEffect(() => {
        stateCallback.current?.(pending || !!failure, pending);
    }, [pending, failure]);

    async function add(file) {
        if (!file || pending || disabled || readOnly || !sessionValid) return;

        retryFile.current = file;
        mutationSequence.current += 1;
        setFailure('');
        setPending(true);
        controller.current = new AbortController();
        try {
            if (!file.type.startsWith('image/') || file.size > 10 * 1024 * 1024)
                throw new Error('Choose an image no larger than 10 MiB.');

            const photo = await service.uploadMaintenanceImage(
                kind,
                { parentType, parentId, taskSeq, section, revision, file },
                controller.current.signal
            );
            if (!mounted.current || initialIdentity.current !== authenticatedIdentity()) return;

            if (parentType === 'request') onChange([...attachmentIds, photo.id], photo);
            else onChange([...attachmentIds, photo.id]);
            retryFile.current = null;
        } catch (error) {
            if (mounted.current && initialIdentity.current === authenticatedIdentity()) {
                if (error.latest) onConflict?.(error);
                setFailure(`${error.message} Retry the upload or discard it before saving.`);
            }
        } finally {
            if (mounted.current) setPending(false);
        }
    }

    async function remove(id) {
        if (pending || disabled || readOnly || !sessionValid) return;

        mutationSequence.current += 1;
        setPending(true);
        setFailure('');
        try {
            const result = await service.removeImage(kind, id, revision);
            if (mounted.current && initialIdentity.current === authenticatedIdentity())
                onChange(
                    attachmentIds.filter((value) => value !== id),
                    result
                );
        } catch (error) {
            if (mounted.current) {
                if (error.latest) onConflict?.(error);
                setFailure(`${error.message} Retry removing the photo before saving.`);
            }
        } finally {
            if (mounted.current) setPending(false);
        }
    }

    if (!sessionValid) return <p>Sign in again to view photos.</p>;

    return (
        <div className="mb-2">
            {displayAsLinks && (
                <div className="text-muted mt-2" style={{ fontSize: '0.875rem' }}>
                    {parentType === 'schedule' ? 'Reference photos' : 'Completion photos'}
                </div>
            )}
            <div className="d-flex flex-wrap gap-2">
                {attachmentIds.map((id, index) => (
                    <div
                        key={id}
                        className="position-relative d-inline-flex"
                        style={displayAsLinks ? undefined : { width: 96, height: 96, flexShrink: 0 }}
                    >
                        {images[id] ? (
                            <a href={images[id]} target="_blank" rel="noreferrer">
                                {displayAsLinks ? `Photo ${index + 1}` : (
                                    <img
                                        src={images[id]}
                                        alt={`Photo ${id}`}
                                        style={{ width: 96, height: 96, objectFit: 'contain' }}
                                    />
                                )}
                            </a>
                        ) : (
                            <span>Loading photo…</span>
                        )}
                        {!readOnly && (
                            <Button
                                size="sm"
                                variant="outline-danger"
                                className="position-absolute top-0 rounded-circle d-flex align-items-center justify-content-center"
                                style={{
                                    '--bs-btn-bg': '#fff',
                                    right: -6,
                                    width: 24,
                                    height: 24,
                                    padding: 0,
                                    lineHeight: 1,
                                }}
                                aria-label={`Remove photo ${id}`}
                                title={`Remove photo`}
                                disabled={disabled || pending}
                                onClick={() => remove(id)}
                            >
                                <span aria-hidden="true">&times;</span>
                            </Button>
                        )}
                    </div>
                ))}
            </div>
            {loadError && (
                <div role="alert">
                    {loadError}{' '}
                    <Button size="sm" onClick={() => setLoadAttempt((value) => value + 1)}>
                        Retry loading photos
                    </Button>
                </div>
            )}
            {!readOnly &&
                (!parentId ? (
                    <p>Save the draft before adding photos.</p>
                ) : (
                    <Form.Control
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif,image/tiff,image/avif"
                        aria-label={`Add ${label} photo`}
                        disabled={disabled || pending || attachmentIds.length >= 5 || !!failure}
                        onChange={(event) => {
                            const file = event.target.files?.[0];
                            event.target.value = '';
                            add(file);
                        }}
                    />
                ))}
            {pending && (
                <div aria-live="polite">
                    <progress aria-label="Photo upload progress" /> Uploading photo…
                </div>
            )}
            {failure && (
                <div role="alert">
                    {failure}{' '}
                    {retryFile.current && (
                        <Button
                            size="sm"
                            disabled={disabled || pending}
                            onClick={() => add(retryFile.current)}
                        >
                            Retry upload
                        </Button>
                    )}{' '}
                    <Button
                        size="sm"
                        disabled={disabled || pending}
                        onClick={() => {
                            retryFile.current = null;
                            setFailure('');
                        }}
                    >
                        Discard failed upload
                    </Button>
                </div>
            )}
        </div>
    );
}
